import { useCallback, useState } from "react";
import { Dialog } from "primereact/dialog";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import { getWriteableDate } from "../../utils";
import { TEXT_SMALL, TEXT_TITLE } from "../../style";

export default function DialogCalendarEvent({
    visible,
    closeDialog,
    mode = "create",
    id,
    title: initialTitle = "",
    description: initialDescription = "",
    start_at,
    end_at,
    onSaved,
    requestAPI,
    showToast,
}) {
    const [form, setForm] = useState({
        title: initialTitle,
        description: initialDescription,
        start_at,
        end_at,
    });
    const [loading, setLoading] = useState(false);

    const save = useCallback(() => {
        if (!form.title?.trim() || !form.start_at || !form.end_at) {
            showToast({ severity: "warn", summary: "Required", detail: "Title, start and end are required", life: 2000 });
            return;
        }

        if (form.end_at.getTime() <= form.start_at.getTime()) {
            showToast({ severity: "warn", summary: "Invalid time", detail: "End must be after start", life: 2000 });
            return;
        }

        const body = {
            title: form.title.trim(),
            description: form.description || null,
            start_at: getWriteableDate({ date: form.start_at }),
            end_at: getWriteableDate({ date: form.end_at }),
        };

        if (mode === "edit") {
            body.id = id;
        }

        requestAPI({
            requestPath: "calendar/events",
            requestMethod: mode === "edit" ? "PATCH" : "POST",
            requestPostBody: body,
            setLoading,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed to save event", life: 2000 }),
            onResponseReceieved: (response, responseCode) => {
                if ((mode === "create" && responseCode === 201) || (mode === "edit" && responseCode === 200)) {
                    showToast({ severity: "success", summary: "Saved", detail: "Event saved", life: 1000 });
                    onSaved?.();
                    closeDialog();
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: response?.error || "Failed to save event", life: 2000 });
                }
            },
        });
    }, [closeDialog, form, id, mode, onSaved, requestAPI, showToast]);

    const remove = useCallback(() => {
        requestAPI({
            requestPath: `calendar/events/${id}`,
            requestMethod: "DELETE",
            parseResponseBody: false,
            setLoading,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed to delete event", life: 2000 }),
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Event deleted", life: 1000 });
                    onSaved?.();
                    closeDialog();
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed to delete event", life: 2000 });
                }
            },
        });
    }, [closeDialog, id, onSaved, requestAPI, showToast]);

    return (
        <Dialog
            header={mode === "edit" ? "Edit Event" : "Add Event"}
            visible={visible}
            className="w-11 max-h-screen"
            onHide={closeDialog}
            pt={{
                headertitle: { className: TEXT_TITLE },
                content: { className: "overflow-visible" },
            }}
        >
            <FloatLabel className="mt-4">
                <InputText
                    id="cal_title"
                    className="w-full"
                    value={form.title}
                    onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                    disabled={loading}
                />
                <label htmlFor="cal_title" className={TEXT_SMALL}>
                    Title
                </label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <InputTextarea
                    id="cal_description"
                    className="w-full"
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                    disabled={loading}
                />
                <label htmlFor="cal_description" className={TEXT_SMALL}>
                    Description
                </label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <Calendar
                    inputId="cal_start"
                    className="w-full"
                    value={form.start_at}
                    onChange={(e) => setForm((prev) => ({ ...prev, start_at: e.value }))}
                    showTime
                    hourFormat="24"
                    dateFormat="dd/mm/yy"
                    showIcon
                    disabled={loading}
                />
                <label htmlFor="cal_start" className={TEXT_SMALL}>
                    Start
                </label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <Calendar
                    inputId="cal_end"
                    className="w-full"
                    value={form.end_at}
                    onChange={(e) => setForm((prev) => ({ ...prev, end_at: e.value }))}
                    showTime
                    hourFormat="24"
                    dateFormat="dd/mm/yy"
                    showIcon
                    disabled={loading}
                />
                <label htmlFor="cal_end" className={TEXT_SMALL}>
                    End
                </label>
            </FloatLabel>

            <div className="flex gap-2 mt-4">
                <Button label="Save" severity="warning" loading={loading} onClick={save} />
                {mode === "edit" && (
                    <ConfirmationWrapper action={remove}>
                        <Button label="Delete" severity="danger" outlined loading={loading} />
                    </ConfirmationWrapper>
                )}
            </div>
        </Dialog>
    );
}
